import { NGX_MAT_DATE_FORMATS } from '@angular-material-components/datetime-picker';
import { NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular-material-components/moment-adapter';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MultiSelect } from '@modeso/twint-lib-core-fe';
import { Observable, Subscription, combineLatest, filter } from 'rxjs';
import { ExportCostFileRequest } from '../../models/interfaces/export-cost-file-request.interface';
import { ExportCostFile } from '../../models/interfaces/export-cost-file.interface';
import { CUSTOM_DATE_FORMATS, CUSTOM_MOMENT_FORMATS } from '../../util/dateFormats.helper';

@Component({
    selector: 'app-export-cost-file',
    templateUrl: './export-cost-file-dialog.component.html',
    styleUrls: ['./export-cost-file-dialog.component.scss'],
    providers: [
        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_MOMENT_FORMATS },
        { provide: NGX_MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    ],
})

// eslint-disable-next-line @angular-eslint/component-class-suffix
export class ExportCostFileDialog implements OnInit, OnDestroy {
    showSeconds = false;
    formGroup: FormGroup | undefined;
    loadingStateHandler$: Subscription | undefined;
    readonly financeParties$: Observable<MultiSelect[]>;
    readonly title: string = 'Export Cost';
    readonly coupons$: Observable<MultiSelect[]>;
    readonly loading$: Observable<boolean>;
    readonly error$: Observable<HttpErrorResponse>;
    readonly success$: Observable<boolean>;
    exportCost: (data: ExportCostFileRequest) => void;

    private selectedCoupons: MultiSelect[] = [];
    private selectedFinanceParties: MultiSelect[] = [];

    constructor(
        public dialogRef: MatDialogRef<ExportCostFileDialog>,
        @Inject(MAT_DIALOG_DATA) public data: ExportCostFile,
    ) {
        this.title = data.title;
        this.coupons$ = data.coupons;
        this.financeParties$ = data.financeParties;
        this.loading$ = data.loading;
        this.error$ = data.error;
        this.success$ = data.success;
        this.exportCost = data.export;

        this.formGroup = new FormGroup({ startFrom: new FormControl(null), endAt: new FormControl(null) });
    }

    ngOnInit() {
        this.loadingStateHandler$ = combineLatest([this.loading$, this.error$, this.success$])
            .pipe(filter(([loading, error, success]) => success && !loading && error == null))
            .subscribe(() => {
                this.dialogRef.close();
            });
    }

    export(): void {
        const data: ExportCostFileRequest = {
            startFrom: this.getUtcStartDateAtZeroSecond(),
            endAt: this.getUtcEndDateAtLastSecondInclusive(),
            coupons: this.selectedCoupons.map((coupon) => coupon.id) ?? [],
            financeParties: this.selectedFinanceParties.map((party) => party.name) ?? [],
        };
        this.exportCost(data);
    }

    private getUtcStartDateAtZeroSecond(): Date | null {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined or null');
        }
        const startFromDateAbstractControl = this.formGroup.get('startFrom');

        if (startFromDateAbstractControl == null) {
            throw new Error('Start date abstract control is undefined or null');
        }

        if (startFromDateAbstractControl.value == null) {
            return null;
        }
        const startDate = new Date(startFromDateAbstractControl.value);

        startDate.setUTCSeconds(0, 0);

        return startDate;
    }

    private getUtcEndDateAtLastSecondInclusive(): Date | null {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined or null');
        }

        const endAtDateAbstractControl = this.formGroup.get('endAt');

        if (endAtDateAbstractControl == null) {
            throw new Error('End date abstract control is undefined or null');
        }

        if (endAtDateAbstractControl.value == null) {
            return null;
        }
        const endDate = new Date(endAtDateAbstractControl.value);

        endDate.setUTCSeconds(59, 999);

        return endDate;
    }

    cancel() {
        this.dialogRef.close();
    }

    selectingFinanceParties(parties: MultiSelect[]): void {
        this.selectedFinanceParties = [...parties];
    }

    selectingCoupons(coupons: MultiSelect[]): void {
        this.selectedCoupons = [...coupons];
    }

    ngOnDestroy(): void {
        if (this.loadingStateHandler$ != null) {
            this.loadingStateHandler$.unsubscribe();
        }
    }

    get startFrom() {
        return this.formGroup?.get('startFrom');
    }

    get endAt() {
        return this.formGroup?.get('endAt');
    }
}
